import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

interface QuantityStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  size?: 'sm' | 'md';
}

// FEATURE (2026-08-31, Jaime): shared +/- quantity stepper so the create form,
// the inline edit form and the shopping rows all use the same control and
// behavior (min clamp at 1, no auto-delete at the minimum).
// UPDATE (2026-08-31, Jaime): the middle value is now a real editable number
// input — the user can type any positive integer directly. It is clamped to
// `min` on commit (blur/Enter), and empty input falls back to the current
// value. Buttons keep working and update the same value.
export default function QuantityStepper({ value, onChange, min = 1, size = 'md' }: QuantityStepperProps) {
  const [draft, setDraft] = useState<string>(String(value));
  const [editing, setEditing] = useState(false);

  // Keep the displayed value in sync when the value changes externally
  // (e.g. +/- buttons, parent-driven updates) while not being edited.
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const parsed = parseInt(draft.trim(), 10);
    if (Number.isNaN(parsed) || parsed < min) {
      setDraft(String(value));
      if (value < min) onChange(min);
      return;
    }
    setDraft(String(parsed));
    if (parsed !== value) onChange(parsed);
  };

  // UPDATE (2026-08-31, Jaime): give the number input breathing room (min 3ch,
  // padding, centered text) and hide native spinner arrows since we ship +/-.
  // Buttons were trimmed slightly so rows stay compact on mobile (~360px).
  // TWEAK (2026-08-31, Jaime): the input had grown too wide — fix it to ~5ch
  // (comfortable for 5 digits) with minimal horizontal padding instead of a
  // min-width that let browsers inflate it.
  // FIX (2026-09-01, Jaime): theme-aware button/icon colors — dark-only slate-300/600 were unreadable in light mode on the card background.
  const btnCls = `rounded border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 ${size === 'sm' ? 'w-6 h-6' : 'w-7 h-7'}`;
  const iconCls = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  const numCls = `text-[var(--v-text-primary)] dark:text-white text-sm text-center bg-transparent outline-none focus:ring-1 focus:ring-sky-500 rounded flex-shrink-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${size === 'sm' ? 'w-[5ch] px-0.5 py-0.5' : 'w-[5ch] px-1 py-1'}`;
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className={btnCls}
        aria-label="Menos"
      >
        <Minus className={iconCls} />
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        value={editing ? draft : String(value)}
        onChange={(e) => {
          setEditing(true);
          setDraft(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'Escape') {
            setEditing(false);
            setDraft(String(value));
          }
        }}
        className={numCls}
        aria-label="Cantidad"
      />
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className={btnCls}
        aria-label="Más"
      >
        <Plus className={iconCls} />
      </button>
    </div>
  );
}
